/**
 * CompetitorAnalysis 组件
 * 职责: 竞争对手分析
 * 
 * 重构后结构:
 * - types.ts: 类型定义
 * - constants.ts: 常量配置
 * - utils.ts: 工具函数
 * - hooks/: 自定义 Hooks
 * - components/: 子组件
 */

import React from 'react';
import { Spin, Empty, Row, Col, Tabs } from 'antd';
import { useParams } from 'react-router-dom';
import { useCompetitorData, useAiAnalysis } from './hooks';
import { KeyMetrics, PriceRangeDistribution, DataTable, AiAnalysisCard } from './components';
import { CompetitorTracking } from './components/CompetitorTracking';

export const CompetitorAnalysis: React.FC = () => {
    const {
        loading,
        data,
        showKeyword,
        setShowKeyword,
        showDate,
        setShowDate,
        showPriceRange,
        setShowPriceRange,
        globalPriceRangeDist,
        bestPriceRange,
        treeData,
    } = useCompetitorData();

    const { aiAnalyzing, aiResult, aiError, handleAiAnalysis } = useAiAnalysis();

    // Tab State
    const [activeTab, setActiveTab] = React.useState('analysis');

    // Get SKU ID for import functionality
    const { id } = useParams<{ id: string }>();
    const skuId = Number(id);

    // Components for Analysis Tab
    const AnalysisContent = () => {
        if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
        if (!data || data.keywords.length === 0) return <Empty description="暂无关键词搜索数据，请先上传" />;

        return (
            <div>
                {/* 关键指标 */}
                <KeyMetrics data={data} />

                {/* 价格区间分布 */}
                <PriceRangeDistribution distribution={globalPriceRangeDist} bestPriceRange={bestPriceRange} />

                {/* 数据透视表 + AI分析 并排 */}
                <Row gutter={16}>
                    <Col span={16}>
                        <DataTable
                            treeData={treeData}
                            showKeyword={showKeyword}
                            showDate={showDate}
                            showPriceRange={showPriceRange}
                            onShowKeywordChange={setShowKeyword}
                            onShowDateChange={setShowDate}
                            onShowPriceRangeChange={setShowPriceRange}
                            skuId={skuId}
                        />
                    </Col>
                    <Col span={8}>
                        <AiAnalysisCard
                            aiAnalyzing={aiAnalyzing}
                            aiResult={aiResult}
                            aiError={aiError}
                            onAnalyze={() => handleAiAnalysis(data)}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    return (
        <div className="competitor-analysis-container">
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                type="card"
                className="competitor-analysis-tabs"
                items={[
                    {
                        key: 'analysis',
                        label: '市场分析',
                        children: <AnalysisContent />,
                    },
                    {
                        key: 'tracking',
                        label: '竞品信息',
                        children: <CompetitorTracking />,
                    }
                ]}
            />
        </div>
    );
};

export default CompetitorAnalysis;
